import { useQuery } from '@tanstack/react-query';
import { leadsService } from '../services';

/**
 * Hook para buscar leads de um contato específico
 *
 * @param contactId - ID do contato
 * @param enabled - Se a query deve ser executada
 * @returns Query result com leads do contato
 *
 * @example
 * ```tsx
 * const { data: contactLeads, isLoading } = useLeadsByContact(contactId);
 * ```
 */
export const useLeadsByContact = (contactId?: string, enabled = true) => {
  return useQuery({
    queryKey: ['leads', 'contact', contactId],
    queryFn: () => leadsService.getLeadsByContact(contactId!),
    enabled: !!contactId && enabled,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook para buscar leads de um cliente específico
 *
 * @param customerId - ID do cliente
 * @param enabled - Se a query deve ser executada
 * @returns Query result com leads do cliente
 *
 * @example
 * ```tsx
 * const { data: customerLeads, isLoading } = useLeadsByCustomer(customerId);
 * ```
 */
export const useLeadsByCustomer = (customerId?: string, enabled = true) => {
  return useQuery({
    queryKey: ['leads', 'customer', customerId],
    queryFn: () => leadsService.getLeadsByCustomer(customerId!),
    enabled: !!customerId && enabled,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook para buscar leads atribuídos a um usuário específico
 *
 * @param userId - ID do usuário
 * @param enabled - Se a query deve ser executada
 * @returns Query result com leads atribuídos ao usuário
 *
 * @example
 * ```tsx
 * const { data: myLeads, isLoading } = useLeadsByAssignee(userId);
 * ```
 */
export const useLeadsByAssignee = (userId?: string, enabled = true) => {
  return useQuery({
    queryKey: ['leads', 'assignee', userId],
    queryFn: () => leadsService.getLeadsByAssignee(userId!),
    enabled: !!userId && enabled,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook para buscar leads de um canal específico
 *
 * @param channelId - ID do canal
 * @param enabled - Se a query deve ser executada
 * @returns Query result com leads do canal
 *
 * @example
 * ```tsx
 * const { data: channelLeads, isLoading } = useLeadsByChannel(channelId);
 * ```
 */
export const useLeadsByChannel = (channelId?: string, enabled = true) => {
  return useQuery({
    queryKey: ['leads', 'channel', channelId],
    queryFn: () => leadsService.getLeadsByChannel(channelId!),
    enabled: !!channelId && enabled,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook para buscar leads de uma fonte específica
 *
 * @param source - Nome da fonte
 * @param enabled - Se a query deve ser executada
 * @returns Query result com leads da fonte
 *
 * @example
 * ```tsx
 * const { data: sourceLeads, isLoading } = useLeadsBySource('website');
 * ```
 */
export const useLeadsBySource = (source?: string, enabled = true) => {
  return useQuery({
    queryKey: ['leads', 'source', source],
    queryFn: () => leadsService.getLeadsBySource(source!),
    enabled: !!source && enabled,
    staleTime: 30 * 1000, // 30 seconds
  });
};
